import { createI18n as createI18nFromLib } from "vue-i18n";
import { defaultDocument } from "@vueuse/core";
import dayjs from "@/utils/dayjs.js";

export const AVAILABLE_LANGUAGES = ["en", "fr"];

const localeModules = import.meta.glob("../locales/*.json");

/** @type {import("vue-i18n").I18n<{}, {}, {}, string, false> | undefined} */
let activeI18n;

export function currentLocale() {
  return activeI18n ? activeI18n.global.locale.value : AVAILABLE_LANGUAGES[0];
}

export function getPreferedLanguage() {
  for (let lang of usePreferredLanguages().value) {
    let short = lang.slice(0, 2);
    if (AVAILABLE_LANGUAGES.includes(short)) return short;
  }
  return AVAILABLE_LANGUAGES[0];
}

/**
 * https://vue-i18n.intlify.dev/guide/installation.html
 * @returns {Promise<import("vue-i18n").I18n<{}, {}, {}, string, false>>}
 */
export async function createI18n() {
  const i18n = createI18nFromLib({
    legacy: false,
    locale: useLocalStorage("locale", getPreferedLanguage()).value,
    fallbackLocale: "en",
    silentFallbackWarn: true,
    fallbackWarn: false,
    missingWarn: false,
    warnHtmlInMessage: "off",
    warnHtmlMessage: false,
    availableLocales: AVAILABLE_LANGUAGES,
    numberFormats: {
      en: {
        percent: {
          style: "percent",
          useGrouping: false,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      },
      fr: {
        percent: {
          style: "percent",
          useGrouping: false,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      },
    },
  });
  activeI18n = i18n;
  await setI18nLanguage(i18n, i18n.global.locale.value);
  watch(i18n.global.locale, async (locale) => await setI18nLanguage(i18n, locale));
  return i18n;
}

/**
 *
 * @param {import("vue-i18n").I18n} i18n
 * @param {string} lang
 * @returns
 */
export async function setI18nLanguage(i18n, lang) {
  const locale = AVAILABLE_LANGUAGES.includes(lang) ? lang : getPreferedLanguage();
  const loader = localeModules[`../locales/${locale}.json`];
  if (!loader) {
    throw new Error(`Locale ${locale} not found`);
  }
  const messages = /** @type {{ default: Record<string, unknown> }} */ (await loader());
  i18n.global.setLocaleMessage(locale, messages.default);
  dayjs.locale(locale);
  useLocalStorage("locale", getPreferedLanguage()).value = locale;
  defaultDocument.documentElement.setAttribute("lang", locale);
  return nextTick();
}

// Dev-only: re-apply an edited locale in place (cache-busted) instead of a full reload, while keeping prod code-splitting.
if (import.meta.hot) {
  import.meta.hot.on("i18n:locale-update", async ({ locale, url }) => {
    if (!activeI18n) return;
    const messages = await import(/* @vite-ignore */ `${url}?t=${Date.now()}`);
    activeI18n.global.setLocaleMessage(locale, messages.default);
  });
}
